import React, { useMemo, useState } from 'react';
import { formatStepError } from './stepError';
import type { DoctorService, ServiceCategory, MedicalService } from '../../../types/doctorOnboarding';

type Props = {
  doctorId: number | null;
  entries: DoctorService[];
  categories: ServiceCategory[];
  medicalServices: MedicalService[];
  onAdd: (body: Omit<DoctorService, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onNext: () => void;
  onBack: () => void;
};

export default function ServicesStep({
  doctorId,
  entries,
  categories,
  medicalServices,
  onAdd,
  onDelete,
  onNext,
  onBack,
}: Props) {
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [serviceId, setServiceId] = useState<number | ''>('');
  const [customName, setCustomName] = useState('');
  const [fee, setFee] = useState<number | ''>('');
  const [duration, setDuration] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filteredServices = useMemo(() => {
    if (categoryId === '') return medicalServices;
    return medicalServices.filter((s) => s.category_id === Number(categoryId));
  }, [categoryId, medicalServices]);

  const getCategoryNameForService = (entry: DoctorService): string | null => {
    if (entry.service_id == null) return null;
    const medical = medicalServices.find((m) => m.id === entry.service_id);
    if (!medical?.category_id) return null;
    const cat = categories.find((c) => c.id === medical!.category_id!);
    return cat?.name ?? null;
  };

  const hasServiceOrCustomName = serviceId !== '' || (customName.trim().length > 0);
  const isValid =
    !!doctorId &&
    hasServiceOrCustomName &&
    (duration === '' || (Number(duration) >= 5 && Number(duration) <= 480));

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || !doctorId) return;
    setLoading(true);
    setError(null);
    try {
      await onAdd({
        doctor_id: doctorId,
        service_id: serviceId === '' ? undefined : Number(serviceId),
        custom_service_name: serviceId === '' ? customName.trim() : undefined,
        service_description: description.trim() || undefined,
        service_fee: fee === '' ? undefined : Number(fee),
        duration: duration === '' ? undefined : Number(duration),
        display_order: entries.length,
        is_active: true,
      } as DoctorService);
      setCategoryId('');
      setServiceId('');
      setCustomName('');
      setFee('');
      setDuration('');
      setDescription('');
    } catch (e: any) {
      setError(formatStepError(e, 'Failed to add service'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="onboarding-section">
        <h3>Services Offered</h3>
        <form onSubmit={handleAdd}>
          <div className="form-group">
            <label>Category</label>
            <select
              value={categoryId === '' ? '' : categoryId}
              onChange={(e) => setCategoryId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Service (dictionary)</label>
            <select
              value={serviceId === '' ? '' : serviceId}
              onChange={(e) => setServiceId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">Select service</option>
              {filteredServices.map((s) => (
                <option key={s.id} value={s.id}>{s.service_name ?? s.name ?? 'Service'}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Custom Service Name (if not in dictionary)</label>
            <input value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="Optional if you selected a service above" />
            {!hasServiceOrCustomName && (
              <span className="field-hint">Select a service from the dictionary or enter a custom name to add.</span>
            )}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Fee (₹)</label>
              <input
                type="number"
                min={0}
                value={fee}
                onChange={(e) => setFee(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="form-group">
              <label>Duration (min)</label>
              <input
                type="number"
                min={5}
                max={480}
                value={duration}
                onChange={(e) => setDuration(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Description (optional)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          {error && <div className="onboarding-error">{error}</div>}
          <button type="submit" className="btn-primary" disabled={!isValid || loading}>
            {loading ? 'Saving...' : 'Add Service'}
          </button>
        </form>
      </div>

      <div className="onboarding-section">
        <h3>Current Services</h3>
        {entries.length === 0 && <p className="onboarding-step-label">No services added.</p>}
        {entries.map((s) => {
          const categoryName = s.service_category_name ?? getCategoryNameForService(s);
          return (
            <div key={s.id} className="onboarding-list-item service-list-item">
              <div className="service-list-item-details">
                <strong>{s.custom_service_name || s.service_name || 'Service'}</strong>
                {categoryName && (
                  <div className="service-list-item-category">Category: {categoryName}</div>
                )}
                {(s.service_fee != null || s.duration != null || (s.service_description && s.service_description.trim())) && (
                  <div className="service-list-item-meta">
                    {(s.service_fee != null || s.duration != null) && (
                      <span>
                        {s.service_fee != null && <>Fee: ₹{s.service_fee}</>}
                        {s.service_fee != null && s.duration != null && ' · '}
                        {s.duration != null && <>Duration: {s.duration} min</>}
                      </span>
                    )}
                    {s.service_description && s.service_description.trim() && (
                      <div className="service-list-item-description">{s.service_description.trim()}</div>
                    )}
                  </div>
                )}
              </div>
              <button type="button" className="btn-secondary" onClick={() => onDelete(s.id)}>
                Remove
              </button>
            </div>
          );
        })}
      </div>

      <div className="step-actions">
        <button type="button" className="btn-secondary" onClick={onBack}>
          Back
        </button>
        <button type="button" className="btn-primary" onClick={onNext}>
          Continue
        </button>
      </div>
    </div>
  );
}
